import {
  ApiResponse,
  Connection,
  SystemAuthorization,
  DryRunResponse,
  EasyflexFinancialAdministration,
  EasyflexAdministration,
  NewConnection,
  RetriggerPreview,
  RunDetails,
  SourceAdminstration,
  SyncLaunchInfo,
  SyncRunRequest,
  SyncRunResponse,
  SynchronisationSchemaUpdate,
  EndpointInfo,
  EndpointDefinition,
  DataStudioQuery,
  DataStudioTaskResponse,
  DataChatRequest,
  DataChatResponse,
  DataStudioApiKey,
  DataStudioApiKeyCreated,
  Module,
} from "../types.ts";

export const SESSION_TOKEN_STORAGE_KEY =
  import.meta.env.VITE_SESSION_TOKEN_STORAGE_KEY ?? "easyflexSessionToken";

export const apiServerUrl = import.meta.env.VITE_API_URL;

if (!apiServerUrl) {
  throw new Error("API server URL is not defined!");
}

export const getResolvedToken = (accessToken?: string): string => {
  if (accessToken && accessToken !== "accessToken") {
    return accessToken;
  }
  if (typeof window !== "undefined") {
    return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY) ?? "";
  }
  return "";
};

export const buildAuthHeaders = (accessToken?: string): Record<string, string> => {
  const token = getResolvedToken(accessToken);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// 403 is intentionally NOT dispatched: it means the user is authenticated but lacks module access.
export const dispatchAuthErrorIfUnauthorized = (status: number): void => {
  if (status === 401) {
    window.dispatchEvent(new CustomEvent("auth-error", { detail: { status } }));
  }
};

/** Carries a message that is already Dutch and safe to show the user. */
export class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/*
 * Error messages shown to the user are always Dutch. The backend sends Dutch
 * `detail` strings, but three other things can end up in a toast if we let
 * them: `response.statusText` ("Bad Gateway"), Pydantic's English validation
 * messages, and raw fetch/browser errors ("Failed to fetch"). Everything below
 * exists so no English reaches the user from those paths.
 *
 * Rule: never derive a message by parsing English text — branch on the HTTP
 * status or on Pydantic's machine-readable error `type` instead. Raw detail
 * always goes to console.error so debuggability is unaffected.
 */

const STATUS_MESSAGES: Record<number, string> = {
  400: "Het verzoek kon niet worden verwerkt. Controleer de ingevoerde gegevens.",
  401: "Je sessie is verlopen. Log opnieuw in via Easyflex.",
  403: "Je hebt geen toegang tot dit onderdeel.",
  404: "De opgevraagde gegevens zijn niet gevonden.",
  409: "Deze actie kan niet worden uitgevoerd, de gegevens zijn inmiddels gewijzigd.",
  413: "De hoeveelheid data is te groot om te verwerken.",
  429: "Te veel verzoeken. Wacht even en probeer het opnieuw.",
};

export const messageForStatus = (status: number): string => {
  const known = STATUS_MESSAGES[status];
  if (known) return known;
  if (status >= 500) return "Er is een serverfout opgetreden. Probeer het later opnieuw.";
  return "Het verzoek kon niet worden verwerkt. Probeer het opnieuw.";
};

// Pydantic v2 error `type` codes are stable across versions; the `msg` text is not.
const VALIDATION_MESSAGES: Record<string, string> = {
  missing: "is verplicht",
  string_type: "moet tekst zijn",
  int_type: "moet een heel getal zijn",
  int_parsing: "moet een heel getal zijn",
  float_type: "moet een getal zijn",
  float_parsing: "moet een getal zijn",
  bool_type: "moet ja of nee zijn",
  bool_parsing: "moet ja of nee zijn",
  list_type: "moet een lijst zijn",
  date_parsing: "moet een geldige datum zijn",
  date_type: "moet een geldige datum zijn",
  datetime_parsing: "moet een geldige datum en tijd zijn",
  datetime_type: "moet een geldige datum en tijd zijn",
  enum: "heeft een ongeldige waarde",
  greater_than: "is te klein",
  greater_than_equal: "is te klein",
  less_than: "is te groot",
  less_than_equal: "is te groot",
  json_invalid: "heeft een ongeldige opmaak",
};

const fieldNameFromLoc = (loc: unknown): string | null => {
  if (!Array.isArray(loc)) return null;
  // Drop the request-part prefix ("body", "query", "path") that FastAPI prepends.
  const parts = loc.filter((p) => typeof p === "string" && !["body", "query", "path"].includes(p));
  return parts.length > 0 ? String(parts[parts.length - 1]) : null;
};

const messageForValidationErrors = (detail: unknown[]): string => {
  const described = detail
    .map((item) => {
      const entry = item as { type?: unknown; loc?: unknown };
      const reason = typeof entry.type === "string" ? VALIDATION_MESSAGES[entry.type] : undefined;
      if (!reason) return null;
      const field = fieldNameFromLoc(entry.loc);
      return field ? `'${field}' ${reason}` : null;
    })
    .filter((s): s is string => s !== null);

  if (described.length === 0) {
    return "De ingevoerde gegevens zijn niet geldig. Controleer je invoer.";
  }
  return `Controleer je invoer: ${described.join(", ")}.`;
};

/**
 * Builds the Dutch message for an already-parsed error body. A Dutch `detail`
 * string from our own backend is the most specific message available, so it
 * wins; anything else falls back to the status code.
 */
export const messageFromErrorBody = (data: unknown, status: number): string => {
  if (data && typeof data === "object") {
    const detail = (data as { detail?: unknown }).detail;

    if (typeof detail === "string" && detail.trim() !== "") {
      return detail;
    }
    // Dict-shaped details (see backend runs.py) carry a human "message" plus a machine "error" code.
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim() !== "") {
        return message;
      }
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return messageForValidationErrors(detail);
    }
  }

  // Never surface statusText or a raw JSON dump — log it and show the status message.
  if (data !== null && data !== undefined && data !== "") {
    console.error("Unrecognised API error body:", data);
  }
  return messageForStatus(status);
};

export const parseErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      return messageFromErrorBody(await response.json(), response.status);
    }
    const text = await response.text();
    if (text) {
      console.error(`Non-JSON API error body (${response.status}):`, text);
    }
  } catch (error) {
    console.error("Could not read API error body:", error);
  }

  return messageForStatus(response.status);
};

/**
 * Dutch message for an error thrown before any response arrived — a dropped
 * connection, DNS failure, or CORS rejection. `error.message` here is
 * browser-generated English ("Failed to fetch"), so it is logged, not shown.
 */
export const messageForThrownError = (error: unknown): string => {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "Het verzoek is geannuleerd.";
  }
  console.error("Request failed before a response was received:", error);
  return "Geen verbinding met de server. Controleer je internetverbinding en probeer het opnieuw.";
};

const getDownloadFilename = (contentDisposition: string | null, fallback: string): string => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
};

/**
 * Low-level helper that attaches the stored session token, performs the HTTP request,
 * and normalises error responses coming from FastAPI.
 */
export async function fetchApi<T>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body: object | string | FormData | null = null,
  accessToken?: string,
): Promise<ApiResponse<T>> {
  const url = `${apiServerUrl}${endpoint}`;

  const headers: Record<string, string> = buildAuthHeaders(accessToken);

  try {
    if (method !== "GET" && body) {
      const isFormData = body instanceof FormData;
      if (!isFormData) {
        body = JSON.stringify(body);
        headers["Content-Type"] = "application/json";
      }
    }

    const response = await fetch(url, {
      method,
      headers,
      body: (body as string | FormData) ?? null,
    });

    // An error response is not guaranteed to be JSON — a gateway can return an
    // HTML page. Parsing must not throw, or a 502 would be reported as a
    // connection failure.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
    let data: any = null;
    if (response.status !== 204) {
      const raw = await response.text();
      if (raw) {
        try {
          data = JSON.parse(raw);
        } catch {
          if (response.ok) {
            console.error(`Could not parse a successful ${response.status} response as JSON:`, raw);
            throw new ApiError("Het antwoord van de server kon niet worden gelezen. Probeer het opnieuw.");
          }
          console.error(`Non-JSON API error body (${response.status}):`, raw);
        }
      }
    }

    if (!response.ok) {
      dispatchAuthErrorIfUnauthorized(response.status);
      // The body is already consumed above, so build the message from it directly.
      throw new ApiError(messageFromErrorBody(data, response.status));
    }

    return { data, error: null };
  } catch (error) {
    console.error("API request failed:", error);
    return {
      data: null,
      // An ApiError already carries a Dutch message; anything else is a
      // pre-response failure whose message is browser-generated English.
      error: error instanceof ApiError ? error.message : messageForThrownError(error),
    };
  }
}

const fetchJsonOrThrow = async <T>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  body: object | string | FormData | null = null,
): Promise<T> => {
  const { data, error } = await fetchApi<T>(endpoint, method, body);
  if (error || data === null || data === undefined) {
    if (!error) {
      console.error(`Request to ${endpoint} returned no data`);
    }
    throw new ApiError(error ?? "Er is een onverwachte fout opgetreden. Probeer het opnieuw.");
  }
  return data;
};

const fetchNumber = (endpoint: string) => fetchJsonOrThrow<number>(endpoint);

/* -------------------------------------------------------------------------- */
/* Easyflex data endpoints                                                    */
/* -------------------------------------------------------------------------- */

export const fetchEasyflexAdministrations = (): Promise<SourceAdminstration[]> =>
  fetchJsonOrThrow<SourceAdminstration[]>(`/easyflex/administrations`);

export const fetchEasyflexFinancialAdministration = (
  administrationCode: number,
  loonjaar: number,
): Promise<EasyflexFinancialAdministration> =>
  fetchJsonOrThrow<EasyflexFinancialAdministration>(
    `/easyflex/financial-administration/${administrationCode}?loonjaar=${loonjaar}`,
  );

export const fetchEasyflexGrootboeken = (administrationCode: number): Promise<SourceAdminstration[]> =>
  fetchJsonOrThrow<SourceAdminstration[]>(`/easyflex/grootboeken/${administrationCode}`);

export const fetchEasyflexBetalingskortingGrootboek = (
  administrationCode: number,
): Promise<{ grootboek: string | null }> =>
  fetchJsonOrThrow<{ grootboek: string | null }>(`/easyflex/betalingskorting-grootboek/${administrationCode}`);

export const fetchEasyflexVATCODES = (): Promise<SourceAdminstration[]> =>
  fetchJsonOrThrow<SourceAdminstration[]>(`/easyflex/vat-codes`);

export const fetchEasyflexJournals = (): Promise<SourceAdminstration[]> =>
  fetchJsonOrThrow<SourceAdminstration[]>(`/easyflex/journals`);

/* -------------------------------------------------------------------------- */
/* Connection management                                                      */
/* -------------------------------------------------------------------------- */

export const fetchConnections = (
  limit?: number,
): Promise<ApiResponse<Connection[]>> =>
  fetchApi<Connection[]>(`/connections/get${limit ? `?limit=${limit}` : ""}`);

export const fetchConnectionsCount = (): Promise<number> => fetchNumber(`/connections/count`);

export const fetchFullConnectionDetails = (id: number): Promise<NewConnection> =>
  fetchJsonOrThrow<NewConnection>(`/connections/get-with-details/${id}`);

export const postCreateConnection = (newConnection: object): Promise<ApiResponse<Connection>> =>
  fetchApi<Connection>(`/connections`, "POST", newConnection);

export const putUpdateConnection = (connectionId: number, connection: object): Promise<ApiResponse<Connection>> =>
  fetchApi<Connection>(`/connections/${connectionId}`, "PUT", connection);

export const deleteConnection = (connectionId: number) =>
  fetchApi(`/connections/${connectionId}`, "DELETE");

export const fetchSyncLaunchInfo = (connectionId: number): Promise<SyncLaunchInfo> =>
  fetchJsonOrThrow<SyncLaunchInfo>(`/connections/sync/${connectionId}/launch-info`);

export const postSyncRun = (
  connectionId: number,
  request: SyncRunRequest = {},
): Promise<ApiResponse<SyncRunResponse>> =>
  fetchApi<SyncRunResponse>(`/connections/sync/${connectionId}`, "POST", request);

export const postDryRun = (
  connectionId: number,
  request: SyncRunRequest = {},
): Promise<ApiResponse<SyncRunResponse>> =>
  fetchApi<SyncRunResponse>(`/connections/sync/${connectionId}`, "POST", { ...request, dryRun: true });

export const fetchDryRunData = (runId: number): Promise<DryRunResponse> =>
  fetchJsonOrThrow<DryRunResponse>(`/runs/${runId}/dry-run`);

export const confirmDryRun = (runId: number) =>
  fetchApi(`/runs/${runId}/dry-run/confirm`, "POST");

export const downloadDryRunModuleExport = async (runId: number, module: string): Promise<void> => {
  const response = await fetch(`${apiServerUrl}/runs/${runId}/dry-run/modules/${encodeURIComponent(module)}/export`, {
    method: "GET",
    headers: buildAuthHeaders(),
  });

  if (!response.ok) {
    dispatchAuthErrorIfUnauthorized(response.status);
    throw new ApiError(await parseErrorMessage(response));
  }

  const blob = await response.blob();
  const filename = getDownloadFilename(response.headers.get("Content-Disposition"), `${module}.xlsx`);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

/* -------------------------------------------------------------------------- */
/* Synchronisation schemas & run history                                     */
/* -------------------------------------------------------------------------- */

export const fetchSyncSchemas = (limit?: number) =>
  fetchApi(`/synchronisation_schemas/get${limit ? `?limit=${limit}` : ""}`);

export const updateSynchronisationSchema = (
  connectionId: number,
  schemaUpdate: SynchronisationSchemaUpdate,
) => fetchApi(`/synchronisation_schemas/update/${connectionId}`, "PUT", schemaUpdate);

export const toggleSyncPause = (connectionId: number) =>
  fetchApi(`/synchronisation_schemas/toggle-pause/${connectionId}`, "PATCH");

export const fetchRuns = (limit?: number) =>
  fetchApi(`/runs/get${limit ? `?limit=${limit}` : ""}`);

export const fetchRunsCount = (): Promise<number> => fetchNumber(`/runs/count`);

export const fetchCompletedRunsCount = (): Promise<number> => fetchNumber(`/runs/completed/count`);

export const retriggerRun = (runId: number) => fetchApi(`/runs/${runId}/retrigger`, "POST");

export const cancelRun = (runId: number) => fetchApi(`/runs/${runId}/cancel`, "POST");

export const retryFailedItems = (runId: number, module: string) =>
  fetchApi(`/runs/${runId}/modules/${module}/retry-failed`, "POST");

export const fetchRetriggerPreview = (runId: number): Promise<RetriggerPreview> =>
  fetchJsonOrThrow<RetriggerPreview>(`/runs/${runId}/retrigger-preview`);

export const fetchRunswithDetails = (limit?: number): Promise<RunDetails[]> =>
  fetchJsonOrThrow<RunDetails[]>(`/runs/details${limit ? `?limit=${limit}` : ""}`);

export const fetchRunDetails = (runId: number): Promise<RunDetails> =>
  fetchJsonOrThrow<RunDetails>(`/runs/${runId}/details`);

/* -------------------------------------------------------------------------- */
/* Exact Online integration                                                   */
/* -------------------------------------------------------------------------- */

export const fetchExactOnlineAuthorizationCode = (
  redirectTo: string = "new-connection",
  systemAuthorizationId?: number,
) => {
  const params = new URLSearchParams({ redirect_to: redirectTo });
  if (systemAuthorizationId) {
    params.set("system_authorization_id", String(systemAuthorizationId));
  }
  return fetchApi(`/exactonline/authorization/code?${params.toString()}`);
};

export const fetchExactSystemAuthorizations = (tenantWide: boolean = false): Promise<SystemAuthorization[]> =>
  fetchJsonOrThrow<SystemAuthorization[]>(
    `/exactonline/system-authorizations${tenantWide ? "?tenant_wide=true" : ""}`
  );

export const fetchExactAdministraties = (systemAuthorizationId: number) =>
  fetchApi(`/exactonline/administraties?system_authorization_id=${systemAuthorizationId}`);

export const fetchExactVATCodes = (division: number, systemAuthorizationId: number) =>
  fetchApi(
    `/exactonline/vat_codes?division=${division}&system_authorization_id=${systemAuthorizationId}`
  );

export const fetchExactModules = (
  division: number,
  systemAuthorizationId: number,
): Promise<ApiResponse<Module[]>> =>
  fetchApi<Module[]>(`/exactonline/modules?division=${division}&system_authorization_id=${systemAuthorizationId}`);

/* -------------------------------------------------------------------------- */
/* DataStudio endpoints                                                       */
/* -------------------------------------------------------------------------- */

export const fetchDataStudioEndpoints = (): Promise<ApiResponse<EndpointInfo[]>> =>
  fetchApi<EndpointInfo[]>(`/datastudio/endpoints`);

export const fetchEndpointDefinition = (endpointName: string): Promise<EndpointDefinition> =>
  fetchJsonOrThrow<EndpointDefinition>(`/datastudio/endpoint/${endpointName}`);

export const queryDataStudio = (query: DataStudioQuery): Promise<ApiResponse<DataStudioTaskResponse>> =>
  fetchApi<DataStudioTaskResponse>(`/datastudio/query`, "POST", query);

export const fetchDataStudioTask = (taskId: string): Promise<ApiResponse<DataStudioTaskResponse>> =>
  fetchApi<DataStudioTaskResponse>(`/datastudio/task/${taskId}`);

export const cancelDataStudioTask = (taskId: string): Promise<ApiResponse<DataStudioTaskResponse>> =>
  fetchApi<DataStudioTaskResponse>(`/datastudio/task/${taskId}/cancel`, "POST");

export const saveDataStudioTask = (taskId: string, filename: string): Promise<ApiResponse<{ id: string }>> =>
  fetchApi<{ id: string }>(`/datastudio/task/${taskId}/save`, "POST", { filename });

export type DataStudioExportSource =
  | { type: "TASK"; task_id: string }
  | { type: "LIBRARY"; library_item_id: string }
  | { type: "GENERATED"; session_id: string; table_name: string };

export interface DataStudioExportRequest {
  administration_code: number;
  source: DataStudioExportSource;
  filename: string;
  format: "XLSX";
}

interface DataStudioExportApiRequest extends DataStudioExportRequest {
  idempotency_key: string;
}

export const createDataStudioExport = (
  request: DataStudioExportApiRequest,
): Promise<ApiResponse<DataStudioTaskResponse>> =>
  fetchApi<DataStudioTaskResponse>(`/datastudio/exports`, "POST", request);

const downloadDataStudioTask = async (taskId: string, fallbackFilename: string): Promise<void> => {
  const response = await fetch(`${apiServerUrl}/datastudio/task/${taskId}/download`, {
    method: "GET",
    headers: buildAuthHeaders(),
  });

  if (!response.ok) {
    dispatchAuthErrorIfUnauthorized(response.status);
    throw new Error(await parseErrorMessage(response));
  }

  const blob = await response.blob();
  const filename = getDownloadFilename(response.headers.get("Content-Disposition"), fallbackFilename);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const waitForDataStudioTask = async (
  taskId: string,
  onUpdate?: (task: DataStudioTaskResponse) => void,
  signal?: AbortSignal,
): Promise<DataStudioTaskResponse> => {
  let consecutiveErrors = 0;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const response = await fetchDataStudioTask(taskId);
    if (response.error || !response.data) {
      // A single failed status call says nothing about the worker, which keeps running regardless.
      if (++consecutiveErrors >= 5) {
        throw new Error(response.error ?? "Taakstatus kon niet worden geladen");
      }
    } else {
      consecutiveErrors = 0;
      onUpdate?.(response.data);
      if (response.data.state === "COMPLETED" || response.data.state === "FAILED" || response.data.state === "CANCELLED") {
        return response.data;
      }
    }

    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Aborted", "AbortError"));
      };
      const timeout = window.setTimeout(() => {
        signal?.removeEventListener("abort", abort);
        resolve();
      }, 1000);
      signal?.addEventListener("abort", abort, { once: true });
    });
  }
};

export const startExportAndDownload = async (
  request: DataStudioExportRequest,
  onUpdate?: (task: DataStudioTaskResponse) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const idempotentRequest = { ...request, idempotency_key: crypto.randomUUID() };
  const created = await createDataStudioExport(idempotentRequest);
  if (created.error || !created.data) {
    throw new Error(created.error ?? "Export kon niet worden gestart");
  }

  const task = await waitForDataStudioTask(created.data.task_id, onUpdate, signal);
  if (task.state !== "COMPLETED") {
    throw new Error(task.error_message ?? "Export kon niet worden voltooid");
  }

  await downloadDataStudioTask(task.task_id, `${request.filename}.xlsx`);
};

export const countDataStudio = (
  query: Pick<DataStudioQuery, "module" | "parameters" | "administration_code">,
): Promise<ApiResponse<{ count: number }>> =>
  fetchApi<{ count: number }>(`/datastudio/count`, "POST", query);

export const deleteGeneratedData = (sessionId: string, tableName: string): Promise<ApiResponse<{ status: string }>> =>
  fetchApi(`/datastudio/session/${sessionId}/data/${encodeURIComponent(tableName)}`, "DELETE");

export const deleteDataSource = (sessionId: string, sourceName: string): Promise<ApiResponse<{ status: string }>> =>
  fetchApi(`/datastudio/session/${sessionId}/source/${encodeURIComponent(sourceName)}`, "DELETE");

export const chatWithData = (request: DataChatRequest): Promise<ApiResponse<DataChatResponse>> =>
  fetchApi<DataChatResponse>(`/datastudio/chat`, "POST", request);

export interface SessionRestoreResponse {
  data_sources: import("../types").DataSource[];
  messages: import("../types").DataChatMessage[];
  generated_data: Record<string, Record<string, unknown>[]>;
}

export const restoreSession = (sessionId: string): Promise<ApiResponse<SessionRestoreResponse>> =>
  fetchApi<SessionRestoreResponse>(`/datastudio/session/${sessionId}/restore`, "GET");

export interface ChatSession {
  id: string;
  name: string;
  created_at: string | null;
  last_opened: string | null;
}

export const fetchSessions = (administrationCode: number): Promise<ApiResponse<ChatSession[]>> =>
  fetchApi<ChatSession[]>(`/datastudio/sessions?administration_code=${administrationCode}`);

export const deleteSession = (sessionId: string): Promise<ApiResponse<null>> =>
  fetchApi(`/datastudio/session/${sessionId}`, "DELETE");

export interface UserLibraryItem {
  id: string;
  filename: string;
  module: string;
  parameters: Record<string, unknown>;
  fields: string[];
  row_count: number | null;
  updated_at: string | null;
}

export const fetchLibraryItems = (administrationCode: number): Promise<ApiResponse<UserLibraryItem[]>> =>
  fetchApi<UserLibraryItem[]>(`/datastudio/library?administration_code=${administrationCode}`);

export interface LibraryPreviewResponse {
  data: Record<string, unknown>[];
  row_count: number | null;
}

export const fetchLibraryItemPreview = (
  libraryId: string,
  administrationCode: number,
  limit = 200,
): Promise<ApiResponse<LibraryPreviewResponse>> =>
  fetchApi<LibraryPreviewResponse>(
    `/datastudio/library/${libraryId}/preview?administration_code=${administrationCode}&limit=${limit}`,
  );

export const deleteLibraryItem = (libraryId: string, administrationCode: number): Promise<ApiResponse<void>> =>
  fetchApi(`/datastudio/library/${libraryId}?administration_code=${administrationCode}`, "DELETE");

export const updateMessageFeedback = (messageId: string, feedback: boolean, feedbackText?: string): Promise<ApiResponse<void>> =>
  fetchApi(`/datastudio/message/${messageId}/feedback`, "PUT", { feedback, feedback_text: feedbackText ?? null });

export const fetchDataStudioApiKeys = (administrationCode: number): Promise<ApiResponse<DataStudioApiKey[]>> =>
  fetchApi(`/datastudio/api-keys?administration_code=${administrationCode}`);

export const createDataStudioApiKey = (
  administrationCode: number,
  name: string,
): Promise<ApiResponse<DataStudioApiKeyCreated>> =>
  fetchApi(`/datastudio/api-keys?administration_code=${administrationCode}`, "POST", { name });

export const revokeDataStudioApiKey = (keyId: string): Promise<ApiResponse<DataStudioApiKey>> =>
  fetchApi(`/datastudio/api-keys/${keyId}/revoke`, "POST");

/* -------------------------------------------------------------------------- */
/* Administration management                                                  */
/* -------------------------------------------------------------------------- */

export const fetchAdministrations = (): Promise<EasyflexAdministration[]> =>
  fetchJsonOrThrow<EasyflexAdministration[]>(`/administrations`);

export const putAdministrationApiKey = (code: number, apiKey: string, isSandbox: boolean) =>
  fetchApi(`/administrations/${code}/api-key`, "PUT", { api_key: apiKey, is_sandbox: isSandbox });

export const deleteAdministrationApiKey = (code: number) =>
  fetchApi(`/administrations/${code}/api-key`, "DELETE");
